import { useEffect, useState } from 'react';
import { CalendarClock, CreditCard, Zap } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { getSupplierColors } from '@/utils/supplierCustomizationCache';

/** Uma modalidade como o banco devolve (get_checkout_payment_options). */
export type CheckoutPaymentOption =
  | { kind: 'pix'; discount_percent?: number }
  | { kind: 'pix_installment'; plan_type: string; entry_percent: number }
  | { kind: 'card_free'; max_installments: number }
  | { kind: 'card_fee'; from_installments: number; max_installments: number };

export interface PaymentOptionRow {
  key: string;
  family: 'pix' | 'parcelado' | 'cartao';
  title: string;
  badge?: { tone: 'now' | 'free' | 'fee'; text: string };
  /** Divisão em percentuais (entrada e saldos) do Pix parcelado, para a barra. */
  split?: number[];
}

/** Só monta os textos: quais modalidades existem para a data e a regra do fornecedor é decidido no banco. */
export function buildPaymentOptionRows(options: CheckoutPaymentOption[]): PaymentOptionRow[] {
  return options.map((option, index) => {
    switch (option.kind) {
      case 'pix': {
        const discount = Number(option.discount_percent) || 0;
        return {
          key: `pix-${index}`, family: 'pix', title: 'Pix à vista',
          badge: discount > 0 ? { tone: 'free', text: `${discount}% de desconto` } : { tone: 'now', text: 'Na hora' },
        };
      }
      case 'pix_installment':
        return {
          key: `parc-${option.plan_type}`, family: 'parcelado', title: `Pix parcelado ${option.plan_type.replace(/_/g, '/')}`,
          split: option.plan_type.split('_').map(Number).filter((n) => Number.isFinite(n) && n > 0),
        };
      case 'card_free':
        return {
          key: `card-free-${index}`, family: 'cartao',
          title: option.max_installments <= 1 ? 'Cartão à vista' : `Cartão em até ${option.max_installments}x`,
          badge: { tone: 'free', text: 'Sem juros' },
        };
      case 'card_fee':
      default: {
        const fee = option as Extract<CheckoutPaymentOption, { kind: 'card_fee' }>;
        return {
          key: `card-fee-${index}`, family: 'cartao',
          title: fee.from_installments <= 1 ? `Cartão em até ${fee.max_installments}x` : `Cartão em ${fee.from_installments}x a ${fee.max_installments}x`,
          badge: { tone: 'fee', text: 'Com juros' },
        };
      }
    }
  });
}

const FAMILY_ICON = { pix: Zap, parcelado: CalendarClock, cartao: CreditCard } as const;

/** Texto curto à direita: divisão do Pix parcelado ("30% + 70%") ou a condição (sem juros, com juros, na hora). */
const rowDetail = (row: PaymentOptionRow): string =>
  row.split && row.split.length > 1 ? row.split.map((part) => `${part}%`).join(' + ') : (row.badge?.text ?? '');

interface CartPaymentOptionsProps {
  supplierId?: string | null;
  /** Data do evento (YYYY-MM-DD, aceita ISO completo). Sem data, os modelos de Pix parcelado não são oferecidos. */
  eventDate?: string | null;
  context?: string;
}

/** "Como pagar" no carrinho: só as modalidades que o cliente pode usar para esta data. */
export function CartPaymentOptions({ supplierId, eventDate, context = 'event' }: CartPaymentOptionsProps) {
  const day = /^\d{4}-\d{2}-\d{2}/.test(String(eventDate ?? '')) ? String(eventDate).slice(0, 10) : null;
  const [data, setData] = useState<CheckoutPaymentOption[]>([]);
  useEffect(() => {
    if (!supplierId) { setData([]); return; }
    let cancelled = false;
    (async () => {
      const { data: result, error } = await (supabase as any).rpc('get_checkout_payment_options', {
        p_supplier_id: supplierId,
        p_event_date: day,
        p_context: context,
      });
      if (cancelled) return;
      setData(!error && Array.isArray(result?.options) ? (result.options as CheckoutPaymentOption[]) : []);
    })();
    return () => { cancelled = true; };
  }, [supplierId, day, context]);

  const rows = buildPaymentOptionRows(data);
  // Ícones na cor do botão do fornecedor (a mesma dos adicionais e dos botões do carrinho).
  const iconColor = getSupplierColors().buttonColor;
  if (rows.length === 0) return null;

  return (
    <div className="space-y-1.5" aria-label="Formas de pagamento disponíveis">
      <p className="text-xs font-semibold text-foreground">Como pagar</p>
      <ul className="divide-y divide-border/60 overflow-hidden rounded-lg border border-border bg-background">
        {rows.map((row) => {
          const Icon = FAMILY_ICON[row.family];
          return (
            <li key={row.key} className="flex items-center justify-between gap-3 px-3 py-2">
              <span className="flex min-w-0 items-center gap-2 text-sm text-foreground">
                <Icon className="h-4 w-4 shrink-0" style={{ color: iconColor }} aria-hidden="true" />
                <span className="truncate">{row.title}</span>
              </span>
              <span className="shrink-0 text-[11px] text-muted-foreground">{rowDetail(row)}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export default CartPaymentOptions;
